/**
 * 📦 RESOURCES
 *
 * All resource definitions for the game.
 * Resources unlock progressively as players build/research.
 *
 * Tiers:
 *   1: Starting (wood, stone, food)
 *   2: Early (gold, iron, population)
 *   3: Knowledge (knowledge, culture)
 *   4: Advanced (steel, coal, gems)
 *   5: Industrial (tools, machinery)
 *   6: Science (science, energy)
 *   7: Magic (magic, artifacts, crystals)
 */

import { DUNGEON_RESOURCES } from '../dungeon/resources';

export const resources = {
  // === TIER 1: STARTING RESOURCES ===
  wood: {
    id: 'wood',
    name: 'Wood',
    icon: '🌲',
    tier: 1,
    description: 'Basic building material harvested from forests.',
    color: '#8B4513',
    clickable: true,  // Can be gathered by clicking
    unlockHint: 'Available from start',
  },
  stone: {
    id: 'stone',
    name: 'Stone',
    icon: '🗻',
    tier: 1,
    description: 'Solid foundation material quarried from the earth.',
    color: '#808080',
    clickable: true,
    unlockHint: 'Available from start',
  },
  food: {
    id: 'food',
    name: 'Food',
    icon: '🌾',
    tier: 1,
    description: 'Sustenance for your people. Required to grow population.',
    color: '#DAA520',
    clickable: true,
    unlockHint: 'Available from start',
  },

  // === TIER 2: EARLY RESOURCES ===
  gold: {
    id: 'gold',
    name: 'Gold',
    icon: '💰',
    tier: 2,
    description: 'Precious currency for trade and hiring.',
    color: '#FFD700',
    clickable: false,
    unlockHint: 'Produced by Markets and trade buildings',
  },
  iron: {
    id: 'iron',
    name: 'Iron',
    icon: '🔩',
    tier: 2,
    description: 'Strong metal for tools and construction.',
    color: '#708090',
    clickable: false,
    unlockHint: 'Research "Mining" technology',
  },
  population: {
    id: 'population',
    name: 'Population',
    icon: '👥',
    tier: 2,
    description: 'Citizens of your kingdom. More people = more productivity.',
    color: '#4169E1',
    clickable: false,
    unlockHint: 'Build Houses and provide Food',
  },

  // === TIER 3: KNOWLEDGE RESOURCES ===
  knowledge: {
    id: 'knowledge',
    name: 'Knowledge',
    icon: '📜',
    tier: 3,
    description: 'Wisdom from study. Required to research technologies.',
    color: '#9370DB',
    clickable: false,
    unlockHint: 'Research "Writing" technology',
  },
  culture: {
    id: 'culture',
    name: 'Culture',
    icon: '🎭',
    tier: 3,
    description: 'Art and traditions. Makes your kingdom prestigious.',
    color: '#FF69B4',
    clickable: false,
    unlockHint: 'Research "Writing" technology',
  },

  // === TIER 4: ADVANCED RESOURCES ===
  steel: {
    id: 'steel',
    name: 'Steel',
    icon: '⚔️',
    tier: 4,
    description: 'Refined iron alloy. Stronger than iron.',
    color: '#B0C4DE',
    clickable: false,
    unlockHint: 'Research "Steel Working" technology',
  },
  coal: {
    id: 'coal',
    name: 'Coal',
    icon: '⚫',
    tier: 4,
    description: 'Fossil fuel for industry. Powers forges.',
    color: '#2F4F4F',
    clickable: false,
    unlockHint: 'Research "Deep Mining" technology',
  },
  gems: {
    id: 'gems',
    name: 'Gems',
    icon: '💎',
    tier: 4,
    description: 'Precious stones for jewelry and enchanting.',
    color: '#00CED1',
    clickable: false,
    unlockHint: 'Research "Gem Cutting" technology',
  },

  // === TIER 5: INDUSTRIAL RESOURCES ===
  tools: {
    id: 'tools',
    name: 'Tools',
    icon: '🔧',
    tier: 5,
    description: 'Crafted implements that improve worker efficiency.',
    color: '#CD853F',
    clickable: false,
    unlockHint: 'Research "Tool Making" technology',
  },
  machinery: {
    id: 'machinery',
    name: 'Machinery',
    icon: '⚙️',
    tier: 5,
    description: 'Complex devices that automate production.',
    color: '#696969',
    clickable: false,
    unlockHint: 'Research "Mechanics" technology',
  },

  // === TIER 6: SCIENCE RESOURCES ===
  science: {
    id: 'science',
    name: 'Science',
    icon: '🔬',
    tier: 6,
    description: 'Advanced research. Unlocks cutting-edge tech.',
    color: '#00FF7F',
    clickable: false,
    unlockHint: 'Research "Scientific Method" technology',
  },
  energy: {
    id: 'energy',
    name: 'Energy',
    icon: '⚡',
    tier: 6,
    description: 'Power for machines and magical devices.',
    color: '#FFFF00',
    clickable: false,
    unlockHint: 'Research "Energy Theory" technology',
  },

  // === TIER 7: MAGIC RESOURCES ===
  magic: {
    id: 'magic',
    name: 'Magic',
    icon: '✨',
    tier: 7,
    description: 'Mystical essence from ley lines. Powers enchantments.',
    color: '#8A2BE2',
    clickable: false,
    unlockHint: 'Research "Arcane Arts" technology',
  },
  artifacts: {
    id: 'artifacts',
    name: 'Artifacts',
    icon: '🏺',
    tier: 7,
    description: 'Enchanted relics of immense power. Very rare.',
    color: '#FFD700',
    clickable: false,
    unlockHint: 'Build Artifact Foundry',
  },
  crystals: {
    id: 'crystals',
    name: 'Crystals',
    icon: '🔮',
    tier: 7,
    description: 'Magical formations that amplify arcane energy.',
    color: '#E6E6FA',
    clickable: false,
    unlockHint: 'Research "Crystal Synthesis" technology',
  },

  // === DUNGEON RESOURCES ===
  // Merge dungeon resources for tech tree display
  ...Object.fromEntries(
    Object.entries(DUNGEON_RESOURCES).map(([key, dungeonRes]) => [
      key,
      {
        ...dungeonRes,
        tier: 8, // Assign to a high tier so they appear in advanced sections
        clickable: false,
        unlockHint: 'Found in dungeons',
      }
    ])
  ),
};

// === HELPER FUNCTIONS ===

export const getResourcesByTier = (tier) =>
  Object.values(resources).filter(r => r.tier === tier);

export const getStarterResources = () =>
  Object.values(resources).filter(r => r.tier === 1);

export const getResourceCount = () =>
  Object.keys(resources).length;

/**
 * Get all sources for a resource (buildings that produce it)
 * This is called dynamically so it always reflects current building data
 */
export const getResourceSources = (resourceId, buildings) => {
  const sources = [];
  const resource = resources[resourceId];
  
  if (!resource) return sources;
  
  // Check if clickable
  if (resource.clickable) {
    sources.push({
      type: 'click',
      icon: '👆',
      text: 'Click to gather manually',
    });
  }
  
  // Find all buildings that produce this resource
  if (buildings) {
    Object.values(buildings).forEach(building => {
      if (building.baseProduction && building.baseProduction[resourceId]) {
        const amount = building.baseProduction[resourceId];
        sources.push({
          type: 'building',
          id: building.id,
          icon: building.icon,
          text: `${building.name} (+${amount}/s)`,
          tier: building.tier,
          techRequired: building.unlockCondition?.tech || null,
        });
      }
    });
  }
  
  // Sort by tier
  sources.sort((a, b) => (a.tier || 0) - (b.tier || 0));
  
  return sources;
};

export default resources;